import { useMemo, useEffect, useState } from 'react';
import ReactPlayer from 'react-player/file';

import { ProjectFile, SetFileConfig } from 'services/file.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { EnumDragger } from 'components/app/_mediator/app.slice';
import { getVideoUrl, getInternalPlayer } from 'components/app/_helpers/_app.helper';
import { setCurrentPlayerDuration } from 'components/app/editor/timestamp/_mediator/timestamp.slice';
import { setSrPlayerPlayback } from 'components/app/selector/_mediator/selector.slice';
import { useSelectorContext } from 'components/app/selector/_mediator/selector.provider';
import { useSelectorCb } from 'components/app/selector/_mediator/use-selector-cb';
import { usePlayerSelect } from './lib/use-player-select';
import { Clip } from 'components/app/editor/_mediator/editor.slice';

import { EnhancedVideoPlayerRef, OnProgress, Player } from 'components/app/app-components/player/player';

interface Props {
  file?: ProjectFile;
  fileConfig?: SetFileConfig;
  clipData?: Clip;
  setInit: React.Dispatch<React.SetStateAction<boolean>>;
  init: boolean;
  isTsSliderHovered?: boolean;
  isHighlightSensitivityActive?: boolean;
}

export const SrPlayerVideo = ({
  file,
  fileConfig,
  clipData,
  setInit,
  init,
  isTsSliderHovered,
  isHighlightSensitivityActive,
}: Props) => {
  const { srPlayerRef, srListenClip } = useSelectorContext();
  const { srSelectClip } = useSelectorCb();
  const srPlayerPlayback = useAppSelector((state) => state.selector.srPlayerPlayback);
  const activeMenuClip = useAppSelector((state) => state.editor.activeMenuClip);
  const activeClip = useAppSelector((state) => state.editor.activeClip);
  const dragger = useAppSelector((state) => state.app.dragger);

  const [isReadyToSeek, setIsReadyToSeek] = useState(false);
  const [selectedClipInit, setSelectedClipInit] = useState(false);
  const [isBuffer, setIsBuffer] = useState(false);

  const dispatch = useAppDispatch();

  const selectClip = usePlayerSelect();

  const url = useMemo(() => {
    if (!file || !fileConfig) return '';
    return getVideoUrl({ ...file, config: fileConfig });
  }, [file, fileConfig]);

  const blurVideoPlayer = (element: HTMLVideoElement) => element.blur();

  const onInit = (init: boolean) => {
    const duration = init ? srPlayerRef.current!.getDuration() : 0;
    dispatch(setCurrentPlayerDuration(duration));
    setInit(init);
  };

  const handleVideoReady = (player: ReactPlayer) => {
    // Store the player reference for compatibility
    (srPlayerRef as React.MutableRefObject<ReactPlayer | null>).current = player;
    onReady();
  };

  const onReady = () => {
    if (!init) onInit(true);
    const internalVideoPlayer = getInternalPlayer(srPlayerRef.current);
    internalVideoPlayer!.addEventListener('focus', () => blurVideoPlayer(internalVideoPlayer!));
    setIsReadyToSeek(true);
  };

  const onSeek = (seek: number) => {
    if (!srListenClip.current && !activeMenuClip && dragger !== EnumDragger.tsSlider) selectClip(seek);
    srListenClip.current = null;
  };

  const onProgress = ({ playedSeconds }: OnProgress) => {
    if (!srPlayerPlayback || isBuffer) return;
    selectClip(playedSeconds);
  };

  useEffect(() => {
    return () => {
      const internalVideoPlayer = getInternalPlayer(srPlayerRef.current);
      srPlayerRef.current = null;
      if (internalVideoPlayer)
        internalVideoPlayer.removeEventListener('focus', () => blurVideoPlayer(internalVideoPlayer));
    };
  }, []);

  useEffect(() => {
    onInit(false);
  }, [url]);

  useEffect(() => {
    if (selectedClipInit || !isReadyToSeek) return;
    if (activeClip) srSelectClip(activeClip);
    setSelectedClipInit(true);
  }, [isReadyToSeek, activeClip, selectedClipInit]);

  return (
    <Player
      ref={srPlayerRef as React.RefObject<EnhancedVideoPlayerRef>}
      config={{
        attributes: {
          crossOrigin: 'anonymous',
          preload: 'metadata',
        },
      }}
      width="100%"
      height="100%"
      onSeek={onSeek}
      showCustomControls={true}
      progressInterval={250}
      onProgress={onProgress}
      onVideoReady={handleVideoReady}
      onPlay={() => dispatch(setSrPlayerPlayback(true))}
      onPause={() => {
        dispatch(setSrPlayerPlayback(false));
      }}
      onBuffer={() => setIsBuffer(true)}
      onBufferEnd={() => setIsBuffer(false)}
      onEnded={() => dispatch(setSrPlayerPlayback(false))}
      url={url}
      isTsSliderHovered={isTsSliderHovered}
      isHighlightSensitivityActive={isHighlightSensitivityActive}
      fileConfig={fileConfig}
      clipData={clipData}
      rotate={fileConfig?.rotate}
    />
  );
};
